/**
 * 法律知识库组件
 * 支持文档上传、语义搜索、RAG 问答
 */

import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, FolderOpen, Search, MessageSquare, Trash2, FileText, Lightbulb, BookMarked } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

// API 基础 URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

// 文档类型
interface KnowledgeDocument {
    id: string;
    name: string;
    size: number;
    uploadedAt: number;
    chunkCount: number;
}

// 搜索结果
interface SearchResultItem {
    content: string;
    sourceName: string;
    sourceId: string;
    pageNumber?: number;
    relevance: number;
}

// 引用
interface Citation {
    sourceId: string;
    sourceName: string;
    text: string;
    pageNumber?: number;
    relevance: number;
}

// Q&A 结果
interface QAResult {
    answer: string;
    citations: Citation[];
    model: string;
}

interface KnowledgeBaseProps {
    isOpen: boolean;
    onClose: () => void;
    userId?: string;
    currentLanguage?: string;
}

export const KnowledgeBase: React.FC<KnowledgeBaseProps> = ({ isOpen, onClose, userId, currentLanguage = 'en-US' }) => {
    const { t } = useTranslation(currentLanguage);
    // 文档列表
    const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);

    // 上传状态
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<string | null>(null);

    // 搜索状态
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
    const [searching, setSearching] = useState(false);

    // Q&A 状态
    const [question, setQuestion] = useState('');
    const [qaResult, setQaResult] = useState<QAResult | null>(null);
    const [asking, setAsking] = useState(false);

    // 当前标签
    const [activeTab, setActiveTab] = useState<'docs' | 'search' | 'qa'>('docs');

    // 错误状态
    const [error, setError] = useState<string | null>(null);

    // 获取请求头
    const getHeaders = useCallback(() => {
        const headers: Record<string, string> = {};
        if (userId) {
            headers['x-user-id'] = userId;
        }
        return headers;
    }, [userId]);

    // 加载文档列表
    const loadDocuments = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/knowledge/documents`, {
                headers: getHeaders(),
            });
            const data = await response.json();
            if (data.success) {
                setDocuments(data.documents);
            }
        } catch (err) {
            console.error('Failed to load documents:', err);
        }
    }, [getHeaders]);

    // 初始加载
    useEffect(() => {
        if (isOpen) {
            loadDocuments();
        }
    }, [isOpen, loadDocuments]);

    // 上传文档
    const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setUploading(true);
        setUploadProgress('正在上传...');
        setError(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_BASE_URL}/api/knowledge/upload`, {
                method: 'POST',
                headers: getHeaders(),
                body: formData,
            });

            const data = await response.json();

            if (data.success) {
                setUploadProgress(`✓ 已索引 ${data.chunkCount} 个文本块`);
                loadDocuments();
                setTimeout(() => setUploadProgress(null), 3000);
            } else {
                setError(data.message || 'Upload failed');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Upload failed');
        } finally {
            setUploading(false);
            event.target.value = '';
        }
    };

    // 删除文档
    const handleDelete = async (docId: string) => {
        if (!window.confirm('确定要删除这个文档吗？')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/api/knowledge/documents/${docId}`, {
                method: 'DELETE',
                headers: getHeaders(),
            });

            const data = await response.json();
            if (data.success) {
                loadDocuments();
            } else {
                setError(data.message || 'Delete failed');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Delete failed');
        }
    };

    // 语义搜索
    const handleSearch = async () => {
        if (!searchQuery.trim()) return;

        setSearching(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE_URL}/api/knowledge/search`, {
                method: 'POST',
                headers: {
                    ...getHeaders(),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ query: searchQuery, topK: 5 }),
            });

            const data = await response.json();
            if (data.success) {
                setSearchResults(data.results);
            } else {
                setError(data.message || 'Search failed');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Search failed');
        } finally {
            setSearching(false);
        }
    };

    // RAG 问答
    const handleAsk = async () => {
        if (!question.trim()) return;

        setAsking(true);
        setError(null);
        setQaResult(null);

        try {
            const response = await fetch(`${API_BASE_URL}/api/knowledge/ask`, {
                method: 'POST',
                headers: {
                    ...getHeaders(),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ question, topK: 5 }),
            });

            const data = await response.json();
            if (data.success) {
                setQaResult({
                    answer: data.answer,
                    citations: data.citations,
                    model: data.model,
                });
            } else {
                setError(data.message || 'QA failed');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'QA failed');
        } finally {
            setAsking(false);
        }
    };

    // 格式化文件大小
    const formatSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    };

    // 格式化日期
    const formatDate = (timestamp: number) => {
        return new Date(timestamp).toLocaleDateString('zh-CN');
    };

    if (!isOpen) return null;

    return (
        <div className="knowledge-base-panel">
            <div className="kb-header">
                <h3 className="flex items-center gap-2"><BookOpen className="w-4 h-4" /> 法律知识库</h3>
                <button className="close-btn" onClick={onClose}>&times;</button>
            </div>

            {/* 标签切换 */}
            <div className="kb-tabs">
                <button
                    className={`kb-tab ${activeTab === 'docs' ? 'active' : ''}`}
                    onClick={() => setActiveTab('docs')}
                >
                    <FolderOpen className="w-3.5 h-3.5 inline mr-1" /> 文档
                </button>
                <button
                    className={`kb-tab ${activeTab === 'search' ? 'active' : ''}`}
                    onClick={() => setActiveTab('search')}
                >
                    <Search className="w-3.5 h-3.5 inline mr-1" /> 搜索
                </button>
                <button
                    className={`kb-tab ${activeTab === 'qa' ? 'active' : ''}`}
                    onClick={() => setActiveTab('qa')}
                >
                    <MessageSquare className="w-3.5 h-3.5 inline mr-1" /> 问答
                </button>
            </div>

            {error && <div className="kb-error">{error}</div>}

            <div className="kb-content">
                {/* 文档管理 */}
                {activeTab === 'docs' && (
                    <div className="kb-docs">
                        <div className="kb-upload">
                            <label className="upload-btn">
                                <input
                                    type="file"
                                    accept=".pdf,.docx,.doc,.txt,.md"
                                    onChange={handleUpload}
                                    disabled={uploading}
                                />
                                {uploading ? '上传中...' : '+ 上传文档'}
                            </label>
                            {uploadProgress && (
                                <span className="upload-status">{uploadProgress}</span>
                            )}
                        </div>

                        <div className="kb-doc-list">
                            {documents.length === 0 ? (
                                <div className="kb-empty">
                                    暂无文档，请上传 PDF、Word 或文本文件
                                </div>
                            ) : (
                                documents.map(doc => (
                                    <div key={doc.id} className="kb-doc-item">
                                        <div className="doc-info">
                                            <span className="doc-name">{doc.name}</span>
                                            <span className="doc-meta">
                                                {formatSize(doc.size)} · {doc.chunkCount} 块 · {formatDate(doc.uploadedAt)}
                                            </span>
                                        </div>
                                        <button
                                            className="doc-delete"
                                            onClick={() => handleDelete(doc.id)}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* 语义搜索 */}
                {activeTab === 'search' && (
                    <div className="kb-search">
                        <div className="search-input-group">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="输入搜索内容..."
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            />
                            <button onClick={handleSearch} disabled={searching}>
                                {searching ? '搜索中...' : '搜索'}
                            </button>
                        </div>

                        <div className="search-results">
                            {searchResults.map((result, i) => (
                                <div key={i} className="search-result-item">
                                    <div className="result-source">
                                        <FileText className="w-3.5 h-3.5 inline mr-1" /> {result.sourceName}
                                        {result.pageNumber && ` · 第${result.pageNumber}页`}
                                        <span className="result-relevance">
                                            相关度: {(result.relevance * 100).toFixed(0)}%
                                        </span>
                                    </div>
                                    <div className="result-content">{result.content}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* RAG 问答 */}
                {activeTab === 'qa' && (
                    <div className="kb-qa">
                        <div className="qa-input-group">
                            <textarea
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                placeholder="输入您的法律问题..."
                                rows={3}
                            />
                            <button onClick={handleAsk} disabled={asking}>
                                {asking ? t('thinking') : 'Ask'}
                            </button>
                        </div>

                        {qaResult && (
                            <div className="qa-result">
                                <div className="qa-answer">
                                    <h4 className="flex items-center gap-1"><Lightbulb className="w-4 h-4" /> 回答</h4>
                                    <div className="answer-text">{qaResult.answer}</div>
                                </div>

                                {qaResult.citations.length > 0 && (
                                    <div className="qa-citations">
                                        <h4 className="flex items-center gap-1"><BookMarked className="w-4 h-4" /> 引用来源</h4>
                                        {qaResult.citations.map((cite, i) => (
                                            <div key={i} className="citation-item">
                                                <div className="citation-source">
                                                    [{i + 1}] {cite.sourceName}
                                                    {cite.pageNumber && ` · 第${cite.pageNumber}页`}
                                                    <span className="citation-relevance">
                                                        {(cite.relevance * 100).toFixed(0)}%
                                                    </span>
                                                </div>
                                                <div className="citation-text">{cite.text}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <style>{`
                .knowledge-base-panel {
                    position: fixed;
                    right: 0;
                    top: 0;
                    height: 100vh;
                    width: 400px;
                    background: #ffffff;
                    border-left: 1px solid #e5e7eb;
                    display: flex;
                    flex-direction: column;
                    z-index: 100;
                    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.3);
                }
                
                .kb-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 15px 20px;
                    border-bottom: 1px solid #e5e7eb;
                }
                
                .kb-header h3 {
                    margin: 0;
                    color: #111827;
                    font-size: 16px;
                }
                
                .close-btn {
                    background: none;
                    border: none;
                    color: #6b7280;
                    font-size: 20px;
                    cursor: pointer;
                }
                
                .close-btn:hover { color: #111827; }
                
                .kb-tabs {
                    display: flex;
                    border-bottom: 1px solid #e5e7eb;
                }
                
                .kb-tab {
                    flex: 1;
                    padding: 10px;
                    background: transparent;
                    border: none;
                    color: #6b7280;
                    cursor: pointer;
                    font-size: 13px;
                    transition: all 0.3s;
                }
                
                .kb-tab:hover { color: #111827; }
                
                .kb-tab.active {
                    color: #111827;
                    border-bottom: 2px solid #111827;
                }
                
                .kb-error {
                    background: rgba(239, 68, 68, 0.2);
                    color: #ef4444;
                    padding: 10px 15px;
                    margin: 10px;
                    border-radius: 6px;
                    font-size: 13px;
                }
                
                .kb-content {
                    flex: 1;
                    overflow-y: auto;
                    padding: 15px;
                }
                
                .kb-upload {
                    margin-bottom: 15px;
                }
                
                .upload-btn {
                    display: inline-block;
                    padding: 10px 20px;
                    background: #111827;
                    border-radius: 8px;
                    color: #fff;
                    cursor: pointer;
                    font-size: 14px;
                }
                
                .upload-btn input { display: none; }
                
                .upload-status {
                    margin-left: 10px;
                    color: #059669;
                    font-size: 13px;
                }
                
                .kb-empty {
                    color: #6b7280;
                    text-align: center;
                    padding: 30px;
                    font-size: 14px;
                }
                
                .kb-doc-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 12px;
                    background: #f9fafb;
                    border-radius: 8px;
                    border: 1px solid #e5e7eb;
                    margin-bottom: 8px;
                }
                
                .doc-info { flex: 1; }
                
                .doc-name {
                    display: block;
                    color: #111827;
                    font-size: 14px;
                    margin-bottom: 4px;
                }
                
                .doc-meta {
                    color: #6b7280;
                    font-size: 12px;
                }
                
                .doc-delete {
                    background: none;
                    border: none;
                    cursor: pointer;
                    font-size: 16px;
                    opacity: 0.5;
                }
                
                .doc-delete:hover { opacity: 1; }
                
                .search-input-group, .qa-input-group {
                    display: flex;
                    gap: 10px;
                    margin-bottom: 15px;
                }
                
                .search-input-group input,
                .qa-input-group textarea {
                    flex: 1;
                    padding: 10px 12px;
                    background: #fff;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    color: #111827;
                    font-size: 14px;
                }
                
                .search-input-group input:focus,
                .qa-input-group textarea:focus {
                    outline: none;
                    border-color: #111827;
                }
                
                .search-input-group button,
                .qa-input-group button {
                    padding: 10px 20px;
                    background: #111827;
                    border: none;
                    border-radius: 8px;
                    color: #fff;
                    cursor: pointer;
                    white-space: nowrap;
                }
                
                .search-input-group button:disabled,
                .qa-input-group button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                
                .search-result-item, .citation-item {
                    padding: 12px;
                    background: #f9fafb;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    margin-bottom: 10px;
                }
                
                .result-source, .citation-source {
                    color: #6b7280;
                    font-size: 12px;
                    margin-bottom: 8px;
                }
                
                .result-relevance, .citation-relevance {
                    float: right;
                    color: #059669;
                }
                
                .result-content, .citation-text {
                    color: #374151;
                    font-size: 13px;
                    line-height: 1.5;
                }
                
                .qa-answer {
                    background: #f3f4f6;
                    border-radius: 8px;
                    padding: 15px;
                    margin-bottom: 15px;
                }
                
                .qa-answer h4, .qa-citations h4 {
                    color: #111827;
                    font-size: 14px;
                    margin: 0 0 10px 0;
                }
                
                .answer-text {
                    color: #1f2937;
                    font-size: 14px;
                    line-height: 1.6;
                    white-space: pre-wrap;
                }
            `}</style>
        </div >
    );
};

export default KnowledgeBase;
